import { showCustomToast } from 'common/components/CustomToast/utils';
import { useMutation } from '@apollo/client';
import {
  Checkbox,
  DefaultButton,
  ICheckboxStyles,
  Icon,
  PrimaryButton,
  Separator,
  Stack,
  Text,
  TooltipHost,
} from '@fluentui/react';
import { CloseButton } from 'common/components/Buttons';
import { ModalWrapper } from 'common/components/ModalWrapper';
import { TransactionLayout } from 'common/types/globalTypes';
import React, { useState } from 'react';

import { SectionProps } from '../../../..';
import { Trip_trip } from '../../../../__generated__/Trip';
import { TAB_SIZE } from '../../../IntuitiveFlow';
import { useTravelIcon } from '../../../SectionToggle/utils';
import { TripCopy, TripCopyVariables } from './__generated__/TripCopy';
import { useStyles } from './index.styles';
import { sortMapAscendingBySortOrder } from '../../../../utils';

import TRIP_COPY from './TripCopy.graphql';
import TRAVEL_AUTHORIZATION from '../../../../../../../TravelAuthorization.graphql';
interface CloneTripDialogProps {
  tripItemsState: Map<string, SectionProps>;
  tripData: Trip_trip | null | undefined;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setClonedTripId: React.Dispatch<React.SetStateAction<string | undefined>>;
}
let checkBoxStyle: ICheckboxStyles = {
  text: {
    fontSize: '16px',
  },
  label: {
    fontWeight: 'bold',
  },
};

export const CloneTripDialog: React.FC<CloneTripDialogProps> = ({
  tripItemsState,
  tripData,
  setShowDialog,
  setClonedTripId,
}) => {
  const sortedTripItems: Map<string, SectionProps> | null =
    sortMapAscendingBySortOrder(tripItemsState);
  const styles = useStyles();

  const tooltipContent = (item: SectionProps, added: boolean) => {
    return (
      <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign="center">
        {!added && <Icon iconName="add" />}
        <Text> {`${item.name}`}</Text>
      </Stack>
    );
  };

  const { getIcon, getOutlineIcon } = useTravelIcon();
  const [tripSectionLayoutTypes, setTripSectionlayoutTypes] = useState<
    TransactionLayout[]
  >(
    Array.from(tripItemsState.entries()).map(
      ([key]) => key as TransactionLayout
    )
  );
  const [amount, setAmount] = useState<boolean>(true);
  const [notes, setNotes] = useState<boolean>(true);

  const { id, _rowTimestamp, travelAuthorizationId } = { ...tripData };
  const [tripCopy, { loading }] = useMutation<TripCopy, TripCopyVariables>(
    TRIP_COPY,
    {
      errorPolicy: 'all',
      onCompleted(data) {
        setClonedTripId(data?.tripCopy?.trip?.id);
      },
    }
  );

  const getTripCopy = async () => {
    let inputVariable = {
      input: {
        tripId: id!,
        tripTimestamp: _rowTimestamp!,
        tripSectionLayoutTypes: tripSectionLayoutTypes,
        isAmountIncluded: amount,
        isNotesIncluded: notes,
      },
    };
    const { errors } = await tripCopy({
      variables: inputVariable,
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: TRAVEL_AUTHORIZATION,
          variables: {
            id: travelAuthorizationId,
          },
        },
      ],
    });

    if (errors?.length) {
      showCustomToast({
        content: errors[0].message,
        appearance: 'error',
      });
    } else {
      showCustomToast({
        content: 'Trip is cloned successfully',
        appearance: 'success',
      });
    }
    setShowDialog(false);
  };

  const BODY_TEXT = 'Sections to include:';
  const isDisabled = loading || !tripSectionLayoutTypes.length;

  return (
    <>
      <ModalWrapper
        isOpen
        onDismiss={() => {}}
        containerClassName={styles.modalContainer}
      >
        <Stack>
          <Stack
            horizontal
            horizontalAlign={'space-between'}
            tokens={{ padding: '20px 20px 10px 20px' }}
          >
            <Text variant={'xLargePlus'}>Create Trip (Clone)</Text>
            <CloseButton onClick={() => setShowDialog(false)} />
          </Stack>
          <Separator />
          <Stack tokens={{ childrenGap: 30, padding: '10px 20px' }}>
            <Text variant="xLarge">{BODY_TEXT}</Text>
            <Stack
              horizontal
              tokens={{
                childrenGap: 20,
                padding: '20px 0px',
              }}
            >
              {sortedTripItems
                ? Array.from(sortedTripItems?.entries()).map(([key, item]) => {
                    const tripItemAdded = tripSectionLayoutTypes.includes(
                      key as TransactionLayout
                    );
                    return (
                      <TooltipHost
                        key={item.id}
                        content={tooltipContent(item, tripItemAdded)}
                      >
                        <Stack
                          className={
                            tripItemAdded
                              ? styles.tabContainerSelected
                              : styles.tabContainer
                          }
                          onClick={() => {
                            if (tripItemAdded) {
                              setTripSectionlayoutTypes(
                                tripSectionLayoutTypes.filter(
                                  (itemKey) => itemKey !== key
                                )
                              );
                            } else {
                              setTripSectionlayoutTypes([
                                ...tripSectionLayoutTypes,
                                key as TransactionLayout,
                              ]);
                            }
                          }}
                        >
                          <Stack className={styles.toggleButton}>
                            {tripItemAdded
                              ? getIcon(key as TransactionLayout, TAB_SIZE)
                              : getOutlineIcon(
                                  key as TransactionLayout,
                                  TAB_SIZE
                                )}
                          </Stack>
                        </Stack>
                      </TooltipHost>
                    );
                  })
                : null}
            </Stack>
            <Stack
              horizontal
              horizontalAlign="center"
              verticalAlign="start"
              tokens={{ childrenGap: 30, padding: '20px 0px' }}
            >
              <Stack horizontal tokens={{ childrenGap: 20 }} grow>
                <Checkbox
                  label="Include Amounts"
                  defaultChecked
                  onChange={(ev, value) => {
                    setAmount(!!value);
                  }}
                  styles={checkBoxStyle}
                />
                <Checkbox
                  label="Notes"
                  defaultChecked
                  onChange={(ev, value) => {
                    setNotes(!!value);
                  }}
                  styles={checkBoxStyle}
                />
              </Stack>
              <Stack horizontal tokens={{ childrenGap: 20 }}>
                <PrimaryButton
                  className={styles.disabledButton}
                  text="Confirm"
                  onClick={() => getTripCopy()}
                  disabled={isDisabled}
                />
                <DefaultButton
                  className={styles.disabledButton}
                  text="Cancel"
                  onClick={() => setShowDialog(false)}
                />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </ModalWrapper>
    </>
  );
};

export { TAB_SIZE };
